import React, { useEffect, useState } from 'react';
import DualBarGroupChart from '../components/DualBarGroupChart';
import MultiLineChart from '../components/charts/MultiLineChart';
import sourceColors from '../sourceColors.json';
import { getDisplayName } from '../sourceDisplayNames';
import { CalendarIcon } from '@heroicons/react/solid';

const sites = Object.keys(sourceColors).filter(
  k => k !== 'targets' && k !== 'productCvTargets'
);
const days = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const equipmentList = [
  'Pulveriser',
  'Crusher',
  'Ash Furnace 1',
  'Ash Furnace 2',
  'Analytical Balance',
  'Floor Balance',
  'Oven 1 Old',
  'Oven 2 New',
  'Splitter',
];

function LabInspectionsMeasurements() {
  const [selected, setSelected] = useState([...sites]);
  const [offset, setOffset] = useState(0);
  const [rangeLabel, setRangeLabel] = useState('');
  const [rowData, setRowData] = useState(null);
  const [cvData, setCvData] = useState([]);
  const [ashData, setAshData] = useState([]);
  const [sampleError, setSampleError] = useState({ labels: [], cv: [], ash: [] });

  useEffect(() => {
    if (rowData && rowData.timesatmp) {
      setRangeLabel(rowData.timesatmp);
    } else {
      setRangeLabel('');
    }
  }, [rowData]);

  const prevWeek = () => setOffset(o => o + 1);
  const nextWeek = () => setOffset(o => (o > 0 ? o - 1 : 0));
  const disableNext = offset === 0;

  useEffect(() => {
    let query = `?offset=${offset}`;
    if (selected.length === 1) {
      query += `&source=${encodeURIComponent(selected[0])}`;
    } else if (selected.length > 1) {
      query += `&sources=${selected.map(encodeURIComponent).join(',')}`;
    }
    fetch(`/api/weekly-lab-inspections.php${query}`)
      .then(res => res.json())
      .then(row =>
        setRowData(row && Object.keys(row).length ? row : null)
      )
      .catch(() => setRowData(null));
  }, [selected, offset]);

  useEffect(() => {
    if (!rowData) {
      setCvData([]);
      return;
    }
    const result = [];
    days.forEach(day => {
      const src = rowData[`${day}_source`];
      if (selected.length === 1 && src && !selected.includes(src)) return;
      result.push({
        label: day.charAt(0).toUpperCase() + day.slice(1),
        initial: parseFloat(rowData[`${day}_enprotec_cv`]) || 0,
        current: parseFloat(rowData[`${day}_sgs_cv`]) || 0,
      });
    });
    setCvData(result);
  }, [rowData, selected]);

  useEffect(() => {
    if (!rowData) {
      setAshData([]);
      return;
    }
    const result = [];
    days.forEach(day => {
      const src = rowData[`${day}_source_ash`];
      if (selected.length === 1 && src && !selected.includes(src)) return;
      result.push({
        label: day.charAt(0).toUpperCase() + day.slice(1),
        initial: parseFloat(rowData[`${day}_enprotec_cv_ash`]) || 0,
        current: parseFloat(rowData[`${day}_sgs_cv_ash`]) || 0,
      });
    });
    setAshData(result);
  }, [rowData, selected]);

  useEffect(() => {
    if (!rowData) {
      setSampleError({ labels: [], cv: [], ash: [] });
      return;
    }
    const labels = [];
    const cv = [];
    const ash = [];
    days.forEach(day => {
      const srcCv = rowData[`${day}_source`];
      const srcAsh = rowData[`${day}_source_ash`];
      if (selected.length === 1 && srcCv && !selected.includes(srcCv) && srcAsh && !selected.includes(srcAsh)) return;
      labels.push(day.charAt(0).toUpperCase() + day.slice(1));
      const enpCv = parseFloat(rowData[`${day}_enprotec_cv`]) || 0;
      const sgsCv = parseFloat(rowData[`${day}_sgs_cv`]) || 0;
      cv.push(
        selected.length === 1 && srcCv && !selected.includes(srcCv)
          ? null
          : enpCv
          ? ((sgsCv - enpCv) / enpCv) * 100
          : null
      );
      const enpAsh = parseFloat(rowData[`${day}_enprotec_cv_ash`]) || 0;
      const sgsAsh = parseFloat(rowData[`${day}_sgs_cv_ash`]) || 0;
      ash.push(
        selected.length === 1 && srcAsh && !selected.includes(srcAsh)
          ? null
          : enpAsh
          ? ((sgsAsh - enpAsh) / enpAsh) * 100
          : null
      );
    });
    setSampleError({ labels, cv, ash });
  }, [rowData, selected]);

  const toggle = site =>
    setSelected(s => (s.includes(site) ? s.filter(x => x !== site) : [...s, site]));
  const toggleAll = () =>
    setSelected(s => (s.length === sites.length ? [] : [...sites]));

  const cvMaxValue = cvData.length
    ? Math.max(...cvData.flatMap(d => [d.initial, d.current, 0]))
    : 0;

  const ashMaxValue = ashData.length
    ? Math.max(...ashData.flatMap(d => [d.initial, d.current, 0]))
    : 0;

  const equipmentData = equipmentList.map(eq => {
    const key = eq.replace(/\s+/g, '_');
    const statuses = days.map(day => (rowData ? rowData[`${key}_${day}`] : ''));
    return { name: eq, statuses };
  });

  return (
    <div className="p-4 space-y-4 text-white flex h-full min-h-0 flex-col">
      {/* Site tabs + date */}
      <div className="flex flex-wrap items-center gap-x-10 gap-y-2 border-b border-gray-600 pb-2">
        {sites.map(site => (
          <div
            key={site}
            onClick={() => toggle(site)}
            className="cursor-pointer px-2"
            style={{
              borderBottom: selected.includes(site)
                ? `2px solid ${sourceColors[site]}`
                : 'none',
            }}
          >
            {getDisplayName(site)}
          </div>
        ))}
        <label className="flex items-center space-x-1">
          <input
            type="checkbox"
            className="form-checkbox h-4 w-4 rounded-full text-[#22c55e] bg-[#0d2c3d] border-gray-600 focus:ring-[#22c55e]"
            checked={selected.length === sites.length}
            onChange={toggleAll}
          />
          <span className="text-sm font-bold text-[#22c55e]">Select All</span>
        </label>
        <div className="flex items-center space-x-2 ml-auto">
          <button
            onClick={prevWeek}
            className="px-2 py-1 bg-[#0d2c3d] rounded shadow hover:bg-[#123b52] transition ease-in-out duration-200"
            aria-label="Previous Week"
          >{'<'}</button>
          <div
            className="px-3 py-1 bg-[#0d2c3d] rounded shadow flex items-center space-x-1 transition transform hover:scale-105"
            title="Record view – click arrows to navigate"
          >
            <CalendarIcon className="h-4 w-4" />
            <span className="text-sm">{rangeLabel}</span>
          </div>
          <button
            onClick={nextWeek}
            disabled={disableNext}
            className="px-2 py-1 bg-[#0d2c3d] rounded shadow hover:bg-[#123b52] disabled:opacity-50 transition ease-in-out duration-200"
            aria-label="Next Week"
          >{'>'}</button>
        </div>
      </div>

      {/* ==== Grid: only last row flexes, scrollable with bottom padding ==== */}
      <div className="grid flex-1 min-h-0 grid-cols-2 gap-4 grid-rows-[auto_auto_1fr]">
        {/* CV */}
        <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 col-span-2 flex h-full flex-col overflow-hidden">
          <h2 className="text-center text-sm mb-2">Laboratory Checks: CV</h2>
          <div className="flex-1 relative min-h-0 h-56 overflow-auto">
            {cvData.length ? (
              <DualBarGroupChart
                data={cvData}
                datasetLabels={['ENPROTEC CV', 'SGS CV']}
                max={Math.max(cvMaxValue, 10)}
                yLabel="CV (MJ/kg)"
              />
            ) : (
              <div className="flex items-center justify-center h-full">No Data</div>
            )}
          </div>
        </div>

        {/* Ash */}
        <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 col-span-2 flex h-full flex-col overflow-hidden">
          <h2 className="text-center text-sm mb-2">Laboratory Checks: Ash</h2>
          <div className="flex-1 relative min-h-0 h-56 overflow-auto">
            {ashData.length ? (
              <DualBarGroupChart
                data={ashData}
                datasetLabels={['ENPROTEC Ash', 'SGS Ash']}
                max={Math.max(ashMaxValue, 10)}
                yLabel="Ash Yield %"
              />
            ) : (
              <div className="flex items-center justify-center h-full">No Data</div>
            )}
          </div>
        </div>

        {/* Equipment Status */}
        <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 flex h-full flex-col overflow-auto min-h-0">
          <h2 className="text-center text-sm mb-2">Laboratory Equipment Status</h2>
          <div className="flex-1 overflow-auto">
            <table className="min-w-full text-xs text-center table-fixed">
              <thead>
                <tr>
                  <th className="px-1">Equipment</th>
                  {['M','Tu','W','Th','F','Sa','Su'].map((d, idx) => (
                    <th key={idx} className="px-1">{d}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {equipmentData.map((eq, idx) => (
                  <tr key={idx} className="even:bg-[#0f3346]">
                    <td className="px-1 py-1 text-left">{eq.name}</td>
                    {eq.statuses.map((st, i) => (
                      <td key={i} className="px-1 py-1">
                        <span
                          className={`inline-block w-3 h-3 rounded-full ${
                            st === 'Online'
                              ? 'bg-green-500'
                              : st === 'Offline'
                              ? 'bg-red-500'
                              : st === 'Standby'
                              ? 'bg-yellow-400'
                              : 'bg-gray-500'
                          }`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 2 Hourly Sample Error */}
        <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 flex flex-col overflow-auto min-h-0">
          <h2 className="text-center text-sm mb-2">2 Hourly Sample Error</h2>
          <div className="flex-1 relative min-h-0">
            {sampleError.labels.length ? (
              <MultiLineChart
                datasets={[
                  { label: 'CV', data: sampleError.cv, color: '#ef4444' },
                  { label: 'ASH', data: sampleError.ash, color: '#22c55e' },
                ]}
                labels={sampleError.labels}
                yLabel="Error %"
                showLegend
                tickStep={20}
              />
            ) : (
              <div className="flex items-center justify-center h-full">No Data</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LabInspectionsMeasurements;
